import { AddinConfigService, AddinNotConfiguredError } from '@outlook-addin/shared';
import { enableProdMode } from '@angular/core';
import { forkJoin, from } from 'rxjs';
import { registerLocaleData } from '@angular/common';
import { JwtHelper, STORAGE_KEYS } from '@outlook-addin/cue-http';
import { bootstrapApplication } from '@angular/platform-browser';
import { AppComponent } from './app/app.component';
import { appConfig, notEnabledConfig } from './app/app.config';
import { environment } from './environments/environment';
import { NotEnabledComponent } from './app/not-enabled/not-enabled.component';

if (environment.production) {
  enableProdMode();
}

const availableCodes = {
  ca: () => [import('@angular/common/locales/ca'), import('@progress/kendo-angular-intl/locales/ca/all')],
  ua: () => [import('@angular/common/locales/ru-UA'), import('@progress/kendo-angular-intl/locales/ru-UA/all')],
  fr: () => [import('@angular/common/locales/fr'), import('@progress/kendo-angular-intl/locales/fr/all')],
  de: () => [import('@angular/common/locales/de'), import('@progress/kendo-angular-intl/locales/de/all')],
  pl: () => [import('@angular/common/locales/pl'), import('@progress/kendo-angular-intl/locales/pl/all')],
  no: () => [import('@angular/common/locales/nb'), import('@progress/kendo-angular-intl/locales/nb/all')],
  nb: () => [import('@angular/common/locales/nb'), import('@progress/kendo-angular-intl/locales/nb/all')],
  en: () => [import('@angular/common/locales/en'), import('@progress/kendo-angular-intl/locales/en/all')],
  cs: () => [import('@angular/common/locales/cs'), import('@progress/kendo-angular-intl/locales/cs/all')],
  es: () => [import('@angular/common/locales/es'), import('@progress/kendo-angular-intl/locales/es/all')],
  ar: () => [import('@angular/common/locales/ar'), import('@progress/kendo-angular-intl/locales/ar/all')],
};

const defaultImport = () => [
  import('@angular/common/locales/en'),
  import('@progress/kendo-angular-intl/locales/en/all'),
];
function getLocaleId(): [Promise<any>, Promise<any>] {
  const userLang =
    localStorage.getItem(STORAGE_KEYS.addinLang) ?? navigator.language ?? (navigator as any).userLanguage;
  const code = userLang.split('-')[0];
  const foundCode = availableCodes[code];
  const returnCode = foundCode ? foundCode() : defaultImport();
  return returnCode;
}

const service = new AddinConfigService();
const notEnabledPath = '/not-enabled';

const angularBootstrap = () =>
  bootstrapApplication(AppComponent, appConfig(service)).catch((err) => console.error(err));

const notEnabledBootstrap = () => {
  window.history.replaceState({}, '', notEnabledPath);
  return bootstrapApplication(NotEnabledComponent, notEnabledConfig(service)).catch((err) => console.error(err));
};

const promises = getLocaleId();
forkJoin(promises.map((x) => from(x))).subscribe((lang) => {
  registerLocaleData(lang[0].default);
  service.bootstrap().then(
    (resolve) => {
      if (service.value.cueSolutionValidLicence === false) {
        notEnabledBootstrap();
        return;
      }

      if (window.location.pathname === notEnabledPath) {
        window.history.replaceState({}, '', '/');
      }

      Office.onReady().then((x) => {
        if (Office.context.mailbox) {
          localStorage.removeItem(STORAGE_KEYS.outlookLoginMode);
          const currentUserToken = localStorage.getItem(STORAGE_KEYS.accessToken);

          if (localStorage.getItem(STORAGE_KEYS.outlookUsername) === Office.context.mailbox.userProfile.emailAddress) {
            //current user is the same as already logged in user
          } else {
            localStorage.setItem(STORAGE_KEYS.outlookUsername, Office.context.mailbox.userProfile.emailAddress);
            if (currentUserToken) {
              const jwtHelper = new JwtHelper();
              if (Office.context.mailbox.userProfile.emailAddress != jwtHelper.getUsernameFromToken(currentUserToken)) {
                localStorage.removeItem(STORAGE_KEYS.accessToken);
                localStorage.removeItem(STORAGE_KEYS.refreshToken);
                localStorage.removeItem(STORAGE_KEYS.accessTokenMicrosoft);
                localStorage.removeItem(STORAGE_KEYS.refreshTokenMicrosoft);
                localStorage.removeItem(STORAGE_KEYS.code);
              }
            } else {
              localStorage.removeItem(STORAGE_KEYS.accessToken);
              localStorage.removeItem(STORAGE_KEYS.refreshToken);
              localStorage.removeItem(STORAGE_KEYS.accessTokenMicrosoft);
              localStorage.removeItem(STORAGE_KEYS.refreshTokenMicrosoft);
              localStorage.removeItem(STORAGE_KEYS.code);
            }
          }
        }
        angularBootstrap();
      });
    },
    (reject) => {
      // debilni O365 js overriduje alert....
      const div = document.createElement('div');
      div.textContent = 'Cannot start application: ' + (reject instanceof Error ? reject.message : reject);
      if (reject instanceof AddinNotConfiguredError && /^https?:\/\//i.test(reject.adminSettingsUrl ?? '')) {
        const link = document.createElement('a');
        link.href = reject.adminSettingsUrl!;
        link.textContent = 'Open CUE Admin';
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        div.append(' ', link);
      }
      document.body.prepend(div);
    }
  );
});
